import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { AuthContext } from "../context/AuthContext";
import {
  complaintService,
  blockService,
  roomService,
  departmentService,
} from "../services/api";
import "./ComplaintForm.css";

const complaintTypes = [
  "PC Hardware",
  "PC Software",
  "Application Issues",
  "Network",
  "Electronics",
  "Plumbing",
  "Other",
];

const ComplaintForm = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [departmentId, setDepartmentId] = useState("");
  const [departments, setDepartments] = useState([]);
  const [blockId, setBlockId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [customRoomNumber, setCustomRoomNumber] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // Fetch departments on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const resp = await departmentService.getAll();
        const data = resp.data;
        const list = Array.isArray(data) ? data : data?.data || [];
        setDepartments(list);
      } catch (err) {
        console.error("Failed to fetch departments:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load departments",
        );
      } finally {
        setLoadingData(false);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch blocks when department is selected
  useEffect(() => {
    const fetchBlocks = async () => {
      if (!departmentId) {
        setBlocks([]);
        setBlockId("");
        return;
      }
      setLoadingData(true);
      try {
        const resp = await blockService.getAll({ departmentId });
        const data = resp.data;
        const list = Array.isArray(data) ? data : data?.data || [];
        setBlocks(list);
      } catch (err) {
        console.error("Failed to fetch blocks:", err);
        setError(
          err.response?.data?.message || err.message || "Failed to load blocks",
        );
      } finally {
        setLoadingData(false);
      }
    };
    fetchBlocks();
    setBlockId("");
    setRoomId("");
    setFilteredRooms([]);
  }, [departmentId]);

  // Fetch rooms when block is selected
  useEffect(() => {
    const fetchRooms = async () => {
      if (!blockId) {
        setFilteredRooms([]);
        setRoomId("");
        return;
      }
      setLoadingData(true);
      try {
        const resp = await roomService.getAll({ blockId });
        const data = resp.data;
        const list = Array.isArray(data) ? data : data?.data || [];
        setFilteredRooms(list);
      } catch (err) {
        console.error("Failed to fetch rooms:", err);
        setError(
          err.response?.data?.message || err.message || "Failed to load rooms",
        );
      } finally {
        setLoadingData(false);
      }
    };
    fetchRooms();
    setRoomId("");
  }, [blockId]);
  const [type, setType] = useState(complaintTypes[0]);
  const [remarks, setRemarks] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Only allow SuperAdmin and regular Users to create complaints
  if (user && !["SuperAdmin", "User"].includes(user.role)) {
    return (
      <div className="complaint-form-page">
        <div className="complaint-card">
          <div className="complaint-form-header">
            <h2>Access Denied</h2>
            <div className="error-alert">
              You do not have permission to raise complaints.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleFileChange = (e) => {
    setAttachment(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!window.confirm("Are you sure you want to submit this complaint?")) {
      return;
    }
    setError("");
    setLoading(true);

    try {
      const selectedDepartment = departments.find(
        (d) => d._id === departmentId,
      );
      const selectedBlock = blocks.find((b) => b._id === blockId);
      const selectedRoom = filteredRooms.find((r) => r._id === roomId);

      // validate room or custom room number
      const finalRoomNumber =
        roomId === "__manual__"
          ? customRoomNumber
          : selectedRoom?.roomNumber || "";
      if (!finalRoomNumber) {
        setError("Please select a room or enter a room number");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("departmentName", selectedDepartment?.name || "");
      formData.append("programmeName", selectedBlock?.programme?.name || "");
      formData.append("blockName", selectedBlock?.name || "");
      formData.append("roomNumber", finalRoomNumber);
      formData.append("complaintType", type);
      formData.append("remarks", remarks);
      if (attachment) formData.append("attachment", attachment);
      // user details come from token (server should use req.user)
      // but include user id here as convenience
      if (user?.id) formData.append("userId", user.id);

      await complaintService.create(formData);
      toast.success("Complaint raised successfully!", {
        duration: 4000,
        icon: "🚀",
      });
      setTimeout(() => navigate("/dashboard"), 1000);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to raise complaint";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="complaint-form-page">
      <div className="complaint-form-header">
        <h2>Report an Issue</h2>
        <p className="form-subtitle">Submit a new maintenance request</p>
      </div>

      <div className="complaint-card">
        {error && <div className="error-alert">{error}</div>}

        <form onSubmit={handleSubmit} className="form-grid-layout">
          <div className="form-field">
            <label>Department</label>
            <select
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              required
              disabled={loadingData}
            >
              <option value="">Select Department</option>
              {departments.map((dept) => (
                <option key={dept._id} value={dept._id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>Block Name</label>
            <select
              value={blockId}
              onChange={(e) => setBlockId(e.target.value)}
              required
              disabled={!departmentId || loadingData}
            >
              <option value="">Select Block</option>
              {blocks.map((block) => (
                <option key={block._id} value={block._id}>
                  {block.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>Room Number</label>
            <select
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              disabled={!blockId || loadingData}
            >
              <option value="">Select Room</option>
              {filteredRooms.map((room) => {
                const blockName =
                  room.block?.name ||
                  blocks.find((b) => String(b._id) === String(room.block))
                    ?.name ||
                  "";
                const label = blockName
                  ? `${blockName} - ${room.roomNumber}`
                  : room.roomNumber;
                return (
                  <option key={room._id} value={room._id}>
                    {label}
                  </option>
                );
              })}
              <option value="__manual__">Other (Manual Entry)</option>
            </select>
          </div>

          {(roomId === "__manual__" || filteredRooms.length === 0) && (
            <div className="form-field">
              <label>Enter Room Number Manually</label>
              <input
                value={customRoomNumber}
                onChange={(e) => setCustomRoomNumber(e.target.value)}
                placeholder="e.g. R-101"
                required
              />
            </div>
          )}

          <div className="form-field">
            <label>Complaint Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {complaintTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field full-width">
            <label>Description of Issue</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Please describe the problem in detail..."
            />
          </div>

          <div className="form-field full-width">
            <label>Attachment (Optional)</label>
            <div className="file-input-wrapper">
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileChange}
              />
              <span className="file-input-label">
                {attachment
                  ? attachment.name
                  : "Click to upload or drag and drop"}
              </span>
            </div>
          </div>

          <div className="form-field full-width">
            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? "Submitting..." : "Submit Complaint"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ComplaintForm;
